import type { PressEvent } from '@heroui/react';
import type { HTMLAttributes } from 'react';

import { ResponsiveImage, Show } from '@dvb/ui';
import {
  faAngleLeft,
  faSignOut,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  cn,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Navbar,
  NavbarBrand,
  NavbarContent,
} from '@heroui/react';
import { logoutRequest } from '@milestone-portal/api/actions/auth/auth.requests';
import { LOGO_BASE_URL } from '@milestone-portal/common/constants';
import { useAuthStore } from '@milestone-portal/context/useAuthStore';
import { BasicActionBar } from '@milestone-portal/routes/-components/BasicActionBar';
import { SSOLoginButton } from '@milestone-portal/ui/SSOLoginButton/SSOLoginButton';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

interface Props extends HTMLAttributes<HTMLDivElement> {
  hasBackButton?: boolean;
}

export function HeaderBar({ hasBackButton = false }: Props) {
  const { t } = useTranslation();
  const { history, navigate } = useRouter();
  const { authData, removeUser } = useAuthStore();

  const { isPending, mutateAsync: logout } = useMutation({
    mutationFn: (e: PressEvent) => {
      e.continuePropagation();
      return logoutRequest();
    },
    onSuccess: () => {
      removeUser();
      navigate({ to: '/login' });
    },
  });

  return (
    <Navbar maxWidth="full" position="static" shouldHideOnScroll>
      <NavbarContent>
        <Show
          fallback={
            <NavbarBrand
              className="cursor-pointer"
              onClick={() => navigate({ to: '/' })}
            >
              <ResponsiveImage
                alt={t('logo')}
                height={42}
                radius={0}
                ratio={500 / 281}
                src={`${LOGO_BASE_URL}/WebZTech-16-9.webp`}
              />
            </NavbarBrand>
          }
          when={hasBackButton}
        >
          <Button
            // NextUI cannot custom size by default
            className="min-w-0 bg-transparent duration-300 active:scale-[0.97]"
            isIconOnly
            onPress={() => history.back()}
            size="sm"
          >
            <FontAwesomeIcon className="text-[24px]" icon={faAngleLeft} />
          </Button>
        </Show>
      </NavbarContent>

      <NavbarContent className="hidden gap-4 sm:flex" justify="center">
        {/*  */}
      </NavbarContent>

      <NavbarContent justify="end">
        <BasicActionBar />

        {authData?.user ? (
          <Dropdown
            classNames={{
              content: cn`min-w-fit`,
            }}
          >
            <DropdownTrigger>
              <div className="relative">
                <ResponsiveImage
                  alt={t('logo')}
                  className="bg-default-500 cursor-pointer px-px"
                  height={32}
                  radius="50%"
                  src={authData?.user.avatar}
                />
              </div>
            </DropdownTrigger>
            <DropdownMenu
              aria-label={t('profileActions')}
              disabledKeys={isPending ? ['profileInfo', 'logout'] : []}
              variant="flat"
            >
              <DropdownItem
                key="profileInfo"
                onPress={() =>
                  navigate({
                    to: '/profile',
                  })
                }
                startContent={<FontAwesomeIcon icon={faUser} />}
              >
                {t('profileInfo')}
              </DropdownItem>
              <DropdownItem
                className="text-danger"
                closeOnSelect={false}
                color="danger"
                key="logout"
                onPress={logout}
                startContent={<FontAwesomeIcon icon={faSignOut} />}
              >
                {t('logout')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        ) : (
          <SSOLoginButton variant="secondary" />
        )}
      </NavbarContent>
    </Navbar>
  );
}
